"use client";

import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "@/lib/utils";
import { useImageTransformFallback } from "@/hooks/use-image-transform-fallback";
import { SQUARE_SM, imagePresetUrl, type ImagePreset } from "@/lib/images/presets";

/**
 * Avatar primitive — the ONE place a profile picture is resized for the web.
 *
 * WHY THE RESIZE LIVES HERE AND NOT IN `getAvatarUrl`: that function is the
 * *resolve* funnel (bucket-relative path -> absolute public URL) and some of its
 * ~45 consumers legitimately want the untouched original — the OG/social-meta
 * tag on `app/(site)/[username]/page.tsx`, an upload preview, a hero crop.
 * Resizing inside the resolver would be a silent regression with no opt-out.
 * Resizing inside the primitive is opt-out-by-not-using-the-primitive, which is
 * exactly the right granularity: every `<AvatarImage>` renders into a round box
 * of at most a few dozen CSS px, and none of them ever wants 2048 px.
 *
 * @see lib/images/presets.ts — the preset table and why the set is closed
 * @see apps/mobile/components/ui/avatar.tsx — the sibling that already did this
 * @see .tondocs/images/HANDOFF_IMAGE_PIPELINE_STANDARDISATION_2026_07_28.md §6, §8.3
 */

// Root
export const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
      className
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

export type AvatarImageProps = React.ComponentPropsWithoutRef<
  typeof AvatarPrimitive.Image
> & {
  /**
   * Which preset `src` is fetched at. Defaults to {@link SQUARE_SM} (128x128
   * device px = a 64 CSS px box at 2x DPR), which covers **every** `<Avatar>`
   * box in the codebase today — the largest declared root is `h-12` (48 px).
   * Pass `SQUARE_LG` on a box over 64 CSS px (profile hero, seller card).
   *
   * There is no smaller rung to pass on a 16-24 px inline byline, and that is
   * deliberate: those chips are almost always the same face already on screen at
   * 32-48 px, so the "oversize" fetch is a cache HIT, whereas a dedicated tiny
   * rung would be a second stored object per face
   * (`lib/images/presets.ts` — why six rungs and not ten).
   *
   * KNOWN LIMIT — the box class and this prop can drift. The box size lives in
   * the ROOT's `className` (`h-8 w-8`); the fetched size lives here. Editing
   * `h-8` to `h-16` does NOT widen the fetch, and the only symptom is a slightly
   * soft picture — nothing throws, nothing logs. Preventing it structurally
   * would mean measuring the rendered box at runtime (a forced layout per
   * avatar, in lists of dozens) or moving the box size off `className` onto a
   * token prop (a breaking migration for ~22 call sites). Neither is worth it at
   * this scale, so the drift is caught at BUILD time instead:
   * `tests/unit/fitness-avatar-preset-covers-box.test.ts` fails when an
   * `<Avatar>` root declares a box larger than the default preset covers and no
   * explicit `preset` was passed. That guard is a ratchet — raise it, never
   * relax it.
   */
  preset?: ImagePreset;
};

// Image
export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  AvatarImageProps
>(({ className, src, preset = SQUARE_SM, onLoadingStatusChange, ...props }, ref) => {
  // `imagePresetUrl` is a total function over URL STRINGS: `blob:` / `data:`
  // upload previews, `/placeholder.svg` and any non-storage host pass through
  // untouched. React's `img` typings also admit a `Blob` src (an in-memory
  // object with no URL to rewrite), so that case is narrowed out rather than
  // cast away — there is nothing for the CDN to resize.
  const presetSrc = typeof src === "string" && src ? imagePresetUrl(src, preset) : src;
  const { src: effectiveSrc, report } = useImageTransformFallback(
    typeof presetSrc === "string" ? presetSrc : "",
  );

  return (
    <AvatarPrimitive.Image
      ref={ref}
      src={typeof presetSrc === "string" ? effectiveSrc : presetSrc}
      className={cn("object-cover", className)}
      // WHY `onLoadingStatusChange` AND NOT `onError`: Radix does not load the
      // rendered `<img>` — it probes the URL with an off-DOM `new Image()` and
      // renders nothing at all until that probe succeeds. So an `onError` on
      // the element can never fire, and on failure the element is not in the
      // DOM to be retried. This callback is the only error channel the
      // primitive has.
      //
      // That unmount is also why the fallback matters more here than anywhere
      // else: a failed transform does not leave a broken-image icon, it leaves
      // the fallback initials — visually plausible, silently wrong, and
      // invisible to any check that counts rendered images.
      onLoadingStatusChange={(status) => {
        if (status === "error") report();
        onLoadingStatusChange?.(status);
      }}
      {...props}
    />
  );
});
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

// Fallback
export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
